/**
 * Partify EasySearch Adapter Layer
 * ---------------------------------
 * Centralized constants, safe wrappers, and compatibility utilities
 * for the EasySearch → native Partify migration.
 *
 * Phase 1 foundation — loaded globally, coexists with EasySearch runtime.
 * No production behavior is changed by this file's presence.
 */
(function () {
	"use strict";

	/* ═══════════════════════════════════════════════
	   §1  CONSTANTS
	   ═══════════════════════════════════════════════ */

	const FIELD_IDS = Object.freeze({
		year: "easysearch_field_4973",
		make: "easysearch_field_4974",
		model: "easysearch_field_4975",
		submodel: "easysearch_field_28136",
		engine: "easysearch_field_28137",
	});

	const DELIMITER = "easysearch-preselect-delimiter";

	const STORAGE_KEYS = Object.freeze({
		searchTerms: "searchTerms",
		fitmentPreselect: "easysearch-preselect-fitment",
		preselect: "easysearch-preselect",
		forceClear: "es_force_clear",
	});

	const SELECTORS = Object.freeze({
		fitmentContainer: ".model-fitment-easysearch-container",
		fitmentFail: ".easysearch-fitment-fail",
		fitmentSuccess: ".easysearch-fitment-icon-success",
		fitmentHolder: ".easysearch-fitment-holder",
		fitmentHidden: ".easysearch-hidden",
		fitmentWidget: ".easysearch-fitment-widget",
		fitmentSearchWidget: ".easysearch-fitment-search-widget",
		fitmentParams: ".easysearch-fitment-params",
		fitmentHeader: ".easysearch-fitment-header",
		fitmentLink: ".easysearch-fitment-link",
		fitmentLoader: ".easysearch-fitment-loader",
		holder: ".easysearch-holder",
		container: "#easysearch-container",
		btnSearch: "a.easysearch-btn-search",
		customYmmBtn: ".custom-easysearch-ymm-btn",
		actionsHolder: ".easysearch-actions-holder",
		btnHolder: ".easysearch-btn-holder",
		productForm: 'form[action^="/cart/add"]',
	});

	const EVENTS = Object.freeze({
		rendered: "easysearch_rendered",
		loaded: "easysearch_loaded",
		treeInited: "easysearch_tree_inited",
		fitmentRendered: "easysearch_fitment_rendered",
		initNative: "easysearch_init_native_search_page",
		vehicleChanged: "partify:vehicle:changed",
		searchtermsChanged: "partify:searchterms-changed",
		searchtermsUpdated: "searchTermsUpdated",
	});

	/* ═══════════════════════════════════════════════
	   §2  SAFE EASYSEARCH API WRAPPERS
	   ═══════════════════════════════════════════════ */

	function callEasySearch(methodName) {
		const args = Array.prototype.slice.call(arguments, 1);
		if (
			window.easysearch &&
			typeof window.easysearch[methodName] === "function"
		) {
			try {
				return window.easysearch[methodName].apply(window.easysearch, args);
			} catch (e) {
				console.warn(
					"[PartifyAdapter] easysearch." + methodName + "() threw:",
					e,
				);
				return undefined;
			}
		}
		console.warn(
			"[PartifyAdapter] easysearch." + methodName + " not available",
		);
		return undefined;
	}

	function getEasySearchTree() {
		return (window.easysearch && window.easysearch.tree) || null;
	}

	function getEasySearchCurIndex() {
		return (window.easysearch && window.easysearch.curIndex) || "";
	}

	function setEasySearchCurIndex(val) {
		if (window.easysearch) {
			window.easysearch.curIndex = val;
		}
	}

	/* ═══════════════════════════════════════════════
	   §3  CART PROPERTY PARITY — properties[EasySearch]
	   ═══════════════════════════════════════════════ */

	/**
	 * Build the "Year / Make / Model / Submodel / Engine" label string
	 * from the first vehicle in localStorage.searchTerms.
	 * Canonical implementation — used by all add-to-cart flows.
	 */
	function buildEasySearchLabel(vehicleOverride) {
		let vehicle = vehicleOverride;
		if (!vehicle) {
			try {
				const raw = localStorage.getItem(STORAGE_KEYS.searchTerms);
				const arr = JSON.parse(raw);
				if (Array.isArray(arr) && arr.length) vehicle = arr[0];
			} catch (_) {}
		}
		if (!vehicle || typeof vehicle !== "object") return "";

		const clean = function (v) {
			const s = (v == null ? "" : String(v)).trim();
			if (!s || s === "null" || s === "undefined") return "";
			return s;
		};

		return [
			clean(vehicle.year),
			clean(vehicle.make),
			clean(vehicle.model),
			clean(vehicle.submodel),
			clean(vehicle.engine),
		]
			.filter(Boolean)
			.join(" / ");
	}

	/**
	 * Ensure a hidden input for properties[EasySearch] exists in the given form
	 * with the current vehicle label. Idempotent — safe to call repeatedly.
	 */
	function ensureEasySearchCartProperty(form) {
		if (!form) return;
		const label = buildEasySearchLabel();
		if (!label) return;

		let input = form.querySelector('input[name="properties[EasySearch]"]');
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = "properties[EasySearch]";
			form.appendChild(input);
		}
		input.value = label;
	}

	/**
	 * Run ensureEasySearchCartProperty on every cart-add form in the page.
	 */
	function ensureEasySearchCartPropertyOnAllForms() {
		const forms = document.querySelectorAll(SELECTORS.productForm);
		for (let i = 0; i < forms.length; i++) {
			ensureEasySearchCartProperty(forms[i]);
		}
	}

	/* ═══════════════════════════════════════════════
	   §4  FITMENT STATE HELPERS
	   ═══════════════════════════════════════════════ */

	/**
	 * Read EasySearch fitment DOM state — returns a normalized object.
	 * Useful for debugging and for future native fitment comparison.
	 */
	function getFitmentStateFromDOM() {
		const container = document.querySelector(SELECTORS.fitmentContainer);
		if (!container) return { state: "no-container", source: "easysearch-dom" };

		const failEl = container.querySelector(SELECTORS.fitmentFail);
		const successEl = container.querySelector(SELECTORS.fitmentSuccess);
		const holderEl = container.querySelector(SELECTORS.fitmentHolder);
		const isHidden =
			holderEl && holderEl.classList.contains("easysearch-hidden");

		// Check holder-hidden FIRST — EasySearch sets easysearch-hidden when no
		// vehicle is selected. Stale success/fail child elements may still exist
		// inside a hidden holder from a prior session; they must be ignored.
		if (isHidden) return { state: "no-vehicle", source: "easysearch-dom" };
		if (failEl) return { state: "fail", source: "easysearch-dom" };
		if (successEl) return { state: "success", source: "easysearch-dom" };
		return { state: "unknown", source: "easysearch-dom" };
	}

	/* ═══════════════════════════════════════════════
	   §5  FITMENT PRESELECT VALUE BUILDER
	   ═══════════════════════════════════════════════ */

	/**
	 * Build the easysearch-preselect-fitment value string from a vehicle object.
	 * Replicates the delimiter-joined format that EasySearch reads on page load.
	 */
	function buildFitmentPresetValue(vehicle) {
		if (!vehicle) return "";
		const fields = [
			vehicle.year,
			vehicle.make,
			vehicle.model,
			vehicle.submodel,
			vehicle.engine,
		];
		let prefix = [];
		for (let i = 0; i < fields.length; i++) {
			const v = fields[i] == null ? "" : String(fields[i]).trim();
			if (!v) break;
			prefix.push(v);
		}
		if (!prefix.length) return "";
		return prefix
			.map(function (f) {
				return f + DELIMITER;
			})
			.join("");
	}

	/* ═══════════════════════════════════════════════
	   §6  EVENT PAYLOAD BUILDER
	   ═══════════════════════════════════════════════ */

	/**
	 * Build a normalized partify:vehicle:changed payload from a vehicle object.
	 */
	function buildVehicleChangedPayload(vehicle, source) {
		if (!vehicle) return null;
		return {
			year: vehicle.year || "",
			make: vehicle.make || "",
			model: vehicle.model || "",
			submodel: vehicle.submodel || "",
			engine: vehicle.engine || "",
			vin: vehicle.vin || null,
			paintCode: vehicle.paintCode || null,
			ymm: vehicle.ymm || "",
			source: source || "unknown",
		};
	}

	/* ═══════════════════════════════════════════════
	   §8  CANONICAL CART PROPERTY WRITER
	   ═══════════════════════════════════════════════
	   Sole writer for properties[EasySearch] on form-based add-to-cart.
	   Reads from Partify-owned state (localStorage.searchTerms[0]).
	   Also stamps at form submit time for guaranteed freshness.

	   Disable: set window.__PARTIFY_CART_COWRITER = false before DOMContentLoaded.
	*/
	let _cartCowriterTimer = null;
	function scheduleCartCowrite() {
		if (window.__PARTIFY_CART_COWRITER === false) return;
		if (_cartCowriterTimer) clearTimeout(_cartCowriterTimer);
		_cartCowriterTimer = setTimeout(ensureEasySearchCartPropertyOnAllForms, 80);
	}

	function onFormSubmit(e) {
		const form = e.target;
		if (form && form.matches && form.matches(SELECTORS.productForm)) {
			ensureEasySearchCartProperty(form);
		}
	}

	if (window.__PARTIFY_CART_COWRITER !== false) {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", scheduleCartCowrite);
		} else {
			scheduleCartCowrite();
		}
		window.addEventListener(EVENTS.vehicleChanged, scheduleCartCowrite);
		window.addEventListener(EVENTS.searchtermsUpdated, scheduleCartCowrite);
		document.addEventListener("submit", onFormSubmit, true);
	}

	/* ═══════════════════════════════════════════════
	   §9  PDP FITMENT COMPATIBILITY SHIM
	   ═══════════════════════════════════════════════
	   Emits EasySearch-compatible fitment DOM so the existing
	   MutationObserver + checkProductFitmentStatus chain works
	   unchanged when EasySearch is eventually disabled.

	   Feature flag: window.__PARTIFY_NATIVE_FITMENT
	     false (default) — shim API exists but is never auto-called
	     'shadow'        — observes EasySearch mutations, logs state
	     true            — ready for a native resolver to call emitFitmentState()

	   The shim does NOT determine fitment. It only EMITS the DOM
	   contract. A separate fitment resolver (Phase 5+) will call
	   emitFitmentState() with the correct state.
	*/

	/**
	 * Emit EasySearch-compatible fitment DOM into .model-fitment-easysearch-container.
	 * Triggers the existing MutationObserver automatically via DOM insertion.
	 *
	 * @param {'success'|'fail'|'no-vehicle'} state
	 * @param {object|null} vehicle - { year, make, model, submodel, engine }
	 * @param {string} [fitmentLinkHref] - URL for fail-state "Parts That Fit" button
	 */
	function emitFitmentState(state, vehicle, fitmentLinkHref) {
		const container = document.querySelector(SELECTORS.fitmentContainer);
		if (!container) {
			console.warn(
				"[PartifyAdapter] fitment container not found — cannot emit shim",
			);
			return;
		}

		// Remove any previous shim output
		clearFitmentShim(container);

		var vehicleLabel = "";
		if (vehicle) {
			vehicleLabel = [
				vehicle.year,
				vehicle.make,
				vehicle.model,
				vehicle.submodel,
				vehicle.engine,
			]
				.filter(Boolean)
				.join(" ");
		}

		// Shim wrapper — intentionally NOT using easysearch-fitment-widget so EasySearch's
		// fitmentToggleWidget loop never picks it up. The MutationObserver in
		// product-page-customization.liquid only queries inner elements (fitment-fail,
		// fitment-icon-success, fitment-holder) so the outer class doesn't matter.
		var widget = document.createElement("div");
		widget.className = "partify-fitment-shim";
		widget.setAttribute("data-partify-shim", "1");

		if (state === "success") {
			var holder = document.createElement("div");
			holder.className = "easysearch-fitment-holder";
			var icon = document.createElement("div");
			icon.className = "easysearch-fitment-icon-success";
			holder.appendChild(icon);
			widget.appendChild(holder);
		} else if (state === "fail") {
			var holder2 = document.createElement("div");
			holder2.className = "easysearch-fitment-holder";
			var fail = document.createElement("div");
			fail.className = "easysearch-fitment-fail";
			holder2.appendChild(fail);
			widget.appendChild(holder2);

			if (fitmentLinkHref) {
				var link = document.createElement("a");
				link.className = "easysearch-fitment-link";
				link.href = fitmentLinkHref;
				widget.appendChild(link);
			}
		} else {
			// 'no-vehicle' — holder with easysearch-hidden class
			var holder3 = document.createElement("div");
			holder3.className = "easysearch-fitment-holder easysearch-hidden";
			widget.appendChild(holder3);
		}

		container.appendChild(widget);
		// MutationObserver fires automatically from this DOM insertion
	}

	/**
	 * Remove all shim-emitted DOM from the fitment container.
	 */
	function clearFitmentShim(containerOverride) {
		var container =
			containerOverride || document.querySelector(SELECTORS.fitmentContainer);
		if (!container) return;
		var shims = container.querySelectorAll("[data-partify-shim]");
		for (var i = 0; i < shims.length; i++) shims[i].remove();
	}

	/* §9b  Shadow mode observer
	   When __PARTIFY_NATIVE_FITMENT === 'shadow', attach a lightweight
	   observer that logs EasySearch's fitment decisions for later
	   comparison with a native resolver. Zero DOM writes. */

	function initFitmentShadow() {
		if (window.__PARTIFY_NATIVE_FITMENT !== "shadow") return;

		var container = document.querySelector(SELECTORS.fitmentContainer);
		if (!container) return;

		var shadowObserver = new MutationObserver(function () {
			var st = getFitmentStateFromDOM();
			var vehicle = null;
			try {
				var raw = localStorage.getItem(STORAGE_KEYS.searchTerms);
				var arr = JSON.parse(raw);
				if (Array.isArray(arr) && arr.length) vehicle = arr[0];
			} catch (_) {}

			console.log(
				"[PartifyAdapter:shadow] fitment=" +
					st.state +
					" | vehicle=" +
					(vehicle ? vehicle.ymm || "partial" : "none"),
			);
		});

		shadowObserver.observe(container, { childList: true, subtree: true });
		console.log("[PartifyAdapter:shadow] fitment observer attached");
	}

	// Auto-init shadow mode after DOM ready
	if (window.__PARTIFY_NATIVE_FITMENT === "shadow") {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", initFitmentShadow);
		} else {
			initFitmentShadow();
		}
	}

	/* ═══════════════════════════════════════════════
	   §7  PUBLIC API (updated)
	   ═══════════════════════════════════════════════ */

	window.PartifyAdapter = {
		FIELD_IDS: FIELD_IDS,
		DELIMITER: DELIMITER,
		STORAGE_KEYS: STORAGE_KEYS,
		SELECTORS: SELECTORS,
		EVENTS: EVENTS,
		callEasySearch: callEasySearch,
		getEasySearchTree: getEasySearchTree,
		getEasySearchCurIndex: getEasySearchCurIndex,
		setEasySearchCurIndex: setEasySearchCurIndex,
		buildEasySearchLabel: buildEasySearchLabel,
		ensureEasySearchCartProperty: ensureEasySearchCartProperty,
		ensureEasySearchCartPropertyOnAllForms:
			ensureEasySearchCartPropertyOnAllForms,
		getFitmentStateFromDOM: getFitmentStateFromDOM,
		buildFitmentPresetValue: buildFitmentPresetValue,
		buildVehicleChangedPayload: buildVehicleChangedPayload,
		emitFitmentState: emitFitmentState,
		clearFitmentShim: clearFitmentShim,
	};
})();
